import React from "react";
import { Link } from "react-router-dom";
import { Quote, Star, Users, Home, KeyRound, HeartHandshake, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider, useValuation } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { LEAD_TESTIMONIALS, TESTIMONIAL_BG, FEATURES } from "../mock";

const ICONS = { Users, Home, KeyRound, HeartHandshake };

function Content() {
  const { openValuation } = useValuation();

  return (
    <div className="App">
      <Seo title="Testimonials | New Space Lettings, Hackney" description="What landlords and tenants across Hackney and North London say about working with New Space — real client stories and reviews." path="/testimonials" />
      <Header />

      {/* Page hero */}
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-20">
        <img src={TESTIMONIAL_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#16323f]/85" />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 text-center">
          <div className="mb-4 flex justify-center gap-1">
            {Array.from({ length: 5 }).map((_, s) => (
              <Star key={s} size={20} className="text-[#eaa32a]" fill="#eaa32a" />
            ))}
          </div>
          <h1 className="font-display text-4xl font-700 uppercase tracking-wide text-white lg:text-6xl">
            What our clients think&hellip;
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-white/85">
            Real words from landlords and tenants across Hackney and North London who trust New
            Space with their homes and investments.
          </p>
        </div>
      </section>

      {/* Lead long-form testimonials (alternating) */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1150px] space-y-16 px-5 lg:space-y-24">
          {LEAD_TESTIMONIALS.map((t, i) => (
            <div
              key={t.author}
              className={`grid items-center gap-8 lg:grid-cols-5 lg:gap-14 ${
                i % 2 === 1 ? "" : ""
              }`}
            >
              <div className={`lg:col-span-2 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative">
                  <img
                    src={t.photo}
                    onError={(e) => (e.currentTarget.src = t.fallback)}
                    alt={t.author}
                    className="aspect-[4/5] w-full rounded-sm object-cover shadow-xl"
                  />
                  <span className="absolute -bottom-5 left-5 grid h-14 w-14 place-items-center rounded-full bg-[#ec008c] text-white shadow-lg">
                    <Quote size={24} fill="white" />
                  </span>
                </div>
              </div>
              <div className={`lg:col-span-3 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="mb-4 flex gap-1">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={18} className="text-[#eaa32a]" fill="#eaa32a" />
                  ))}
                </div>
                <p className="text-lg leading-relaxed text-gray-700">{t.quote}</p>
                <p className="mt-6 font-display text-2xl font-600 text-[#16323f]">{t.author}</p>
                <p className="text-sm uppercase tracking-widest text-[#35a4a4]">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-[#16323f] py-16">
        <div className="mx-auto flex max-w-[1000px] flex-col items-center px-5 text-center">
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-white lg:text-4xl">
            Better property management guaranteed
          </h2>
          <p className="mt-3 text-white/75">Get in touch for a no-obligation conversation.</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button
              onClick={openValuation}
              className="group flex items-center justify-center gap-2 bg-[#ec008c] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]"
            >
              Request a Valuation
              <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 border border-white/60 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-[#16323f]"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Service blocks */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1200px] gap-10 px-5 md:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => {
            const Icon = ICONS[f.icon];
            return (
              <div key={f.title} className="group text-center">
                <div
                  className="mx-auto grid h-20 w-20 place-items-center rounded-full text-white shadow-lg transition-transform duration-300 group-hover:-translate-y-1.5"
                  style={{ backgroundColor: f.color }}
                >
                  <Icon size={34} strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-display text-xl font-500 text-[#16323f]">{f.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-gray-600">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function Testimonials() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
